import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramVolumetricFlowRate } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { formatNumber } from '../../../utils/format';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.volumetricFlowRate.v1';

type Fields = { velocity: string; area: string; flowRate: string };
const EMPTY: Fields = { velocity: '', area: '', flowRate: '' };

/**
 * Volumetric Flow Rate — Q = V × A
 * Solves for whichever of V / A / Q is missing given the other two.
 */
const AirflowVolumetricFlowRateScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const v = toImp(fields.velocity, 'velocity');
    const a = toImp(fields.area, 'area');
    const q = toImp(fields.flowRate, 'flow');

    if (fields.flowRate === '') {
      setSolvedFor('flowRate');
      set({ flowRate: fromImp(v * a, 'flow', 2) });
    } else if (fields.velocity === '') {
      if (a === 0) {
        setError('Area cannot be zero.');
        return;
      }
      setSolvedFor('velocity');
      set({ velocity: fromImp(q / a, 'velocity', 2) });
    } else if (fields.area === '') {
      if (v === 0) {
        setError('Velocity cannot be zero.');
        return;
      }
      setSolvedFor('area');
      set({ area: fromImp(q / v, 'area', 2) });
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.velocity && { label: 'Velocity', value: fields.velocity, unit: unit('velocity'), emphasis: solvedFor === 'velocity' },
    fields.area && { label: 'Area', value: fields.area, unit: unit('area'), emphasis: solvedFor === 'area' },
    fields.flowRate && { label: 'Flow Rate', value: fields.flowRate, unit: unit('flow'), emphasis: solvedFor === 'flowRate' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Volumetric Flow Rate"
      subtitle="Calculate flow rate, velocity, or duct area using Q = V × A"
      formula={
        <>
          <FormulaLine>Q = V × A</FormulaLine>
          <FormulaNote>Q = Flow Rate ({unit('flow')}), V = Velocity ({unit('velocity')}), A = Area ({unit('area')})</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Q = V × A"
      diagram={<DiagramVolumetricFlowRate />}
      outputSlots={{
        velocity_fpm: fields.velocity ? toImp(fields.velocity, 'velocity') : undefined,
        area_sqft: fields.area ? toImp(fields.area, 'area') : undefined,
        cfm: fields.flowRate ? toImp(fields.flowRate, 'flow') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.velocity_fpm != null && !fields.velocity) {
          patch.velocity = fromImp(values.velocity_fpm, 'velocity', 2);
        }
        if (values.area_sqft != null && !fields.area) {
          patch.area = fromImp(values.area_sqft, 'area', 2);
        }
        if (values.cfm != null && !fields.flowRate) {
          patch.flowRate = fromImp(values.cfm, 'flow', 2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Velocity"
          value={fields.velocity}
          onChangeText={(v) => set({ velocity: v })}
          rightText={unit('velocity')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Area"
          value={fields.area}
          onChangeText={(v) => set({ area: v })}
          rightText={unit('area')}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Flow Rate"
          value={fields.flowRate}
          onChangeText={(v) => set({ flowRate: v })}
          rightText={unit('flow')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowVolumetricFlowRateScreen;
